import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleXmark, faClock } from '@fortawesome/pro-solid-svg-icons';
import { isFuture } from 'date-fns';

import {
  PlayerFieldStatus,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Bench } from '@sorare/core/src/atoms/icons/Bench';
import { Shirt } from '@sorare/core/src/atoms/icons/shirts/Shirt';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { unitMapping } from '@sorare/core/src/lib/style';

import { SubIcon } from '#src/shared/stats/SubIcon/index.tsx';

import type { PlayerGameScoreStatusIcon_playerGameScoreInterface } from './__generated__/index.graphql.ts';

type Props = {
  playerGameScore: PlayerGameScoreStatusIcon_playerGameScoreInterface;
  size?: keyof typeof unitMapping;
};

export const PlayerGameScoreStatusIcon = ({
  playerGameScore,
  size = 1.5,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { scoreStatus, anyPlayerGameStats, anyGame } = playerGameScore;
  const footballPlayerGameStats = isType(anyPlayerGameStats, 'PlayerGameStats')
    ? anyPlayerGameStats
    : null;

  if (footballPlayerGameStats?.gameStarted) {
    return <Shirt size={size} />;
  }
  if (
    (footballPlayerGameStats?.fieldStatus === PlayerFieldStatus.ON_FIELD ||
      footballPlayerGameStats?.fieldStatus === PlayerFieldStatus.SUBSTITUTED) &&
    !footballPlayerGameStats?.gameStarted
  ) {
    return <SubIcon type="sub_in" size={size} />;
  }
  if (footballPlayerGameStats?.fieldStatus === PlayerFieldStatus.ON_BENCH) {
    return <Bench size={size} color="var(--c-nd-600)" />;
  }
  if (scoreStatus === ScoreStatus.DID_NOT_PLAY) {
    return (
      <FontAwesomeIcon
        icon={faCircleXmark}
        color="var(--c-red-400)"
        width={size * 8}
        height={size * 8}
      />
    );
  }
  if (anyGame?.date && isFuture(anyGame.date)) {
    return (
      <FontAwesomeIcon
        icon={faClock}
        aria-label={formatMessage(fantasy.upcoming)}
      />
    );
  }
  return null;
};

PlayerGameScoreStatusIcon.fragments = {
  playerGameScoreInterface: gql`
    fragment PlayerGameScoreStatusIcon_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      scoreStatus
      anyGame {
        id
        date
      }
      anyPlayerGameStats {
        id
        ... on PlayerGameStats {
          id
          fieldStatus
          gameStarted
        }
      }
    }
  ` as TypedDocumentNode<PlayerGameScoreStatusIcon_playerGameScoreInterface>,
};
